import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  AdminEditEntryInput,
  AdminEditEntryResponse,
  AdminEditOpenEntryInput,
  BulkApproveResponse,
  ClockInForInput,
  ClockInForResponse,
  DeleteEntryResponse,
  EditRequestActionResponse,
  ReopenWeekResponse,
  RequestEditInput,
  RequestEditResponse,
  ResolveOpenInput,
  ResolveOpenResponse,
  TimesheetEntryInput,
  TimesheetEntryResponse,
  WeekApprovalPair,
} from './contractsMirror.js';

/**
 * Timesheet mutations (CARVE-07). Every mutation rides `apiFetch` (credentials + CSRF double-submit +
 * tenant header baked in) and sends NO tenantId — the server resolves the tenant from the principal
 * (SEC-11). On success each invalidates the `['timesheets']` cache so the week grid re-syncs.
 *
 * THE GUARDRAIL (D-03 / T-22-payroll): the inline cell edit posts CLOCK times — the SERVER computes
 * the lunch-adjusted hours (applyAutomaticLunchDeduction) and rejects a clock-out-before-clock-in.
 * The SPA NEVER computes lunch/overtime; it sends the raw times and renders the server result.
 */

/** Bulk week-approval — approve N {employee, week} pairs atomically ("Approve N weeks"). */
export function useBulkApproveWeeksMutation() {
  const queryClient = useQueryClient();
  return useMutation<BulkApproveResponse, Error, { weeks: WeekApprovalPair[] }>({
    mutationFn: ({ weeks }) =>
      apiFetchJson<BulkApproveResponse>('/api/v1/timesheets/approve-weeks', {
        method: 'POST',
        body: JSON.stringify({ weeks }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/** Reopen one previously approved {employee, week} pair. */
export function useReopenWeekMutation() {
  const queryClient = useQueryClient();
  return useMutation<ReopenWeekResponse, Error, WeekApprovalPair>({
    mutationFn: ({ employeeId, weekStart }) =>
      apiFetchJson<ReopenWeekResponse>('/api/v1/timesheets/reopen-week', {
        method: 'POST',
        body: JSON.stringify({ employeeId, weekStart }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

export function useBulkReopenWeeksMutation() {
  const queryClient = useQueryClient();
  return useMutation<ReopenWeekResponse[], Error, { weeks: WeekApprovalPair[] }>({
    mutationFn: ({ weeks }) =>
      Promise.all(
        weeks.map(({ employeeId, weekStart }) =>
          apiFetchJson<ReopenWeekResponse>('/api/v1/timesheets/reopen-week', {
            method: 'POST',
            body: JSON.stringify({ employeeId, weekStart }),
            headers: { 'Content-Type': 'application/json' },
          }),
        ),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/**
 * Inline cell edit — create a clock-time entry. The SPA sends the clock-in/clock-out times; the SERVER
 * applies the lunch deduction + the clock validation and returns the adjusted hours (math preserved).
 */
export function useEntryMutation() {
  const queryClient = useQueryClient();
  return useMutation<TimesheetEntryResponse, Error, TimesheetEntryInput>({
    mutationFn: (input) =>
      apiFetchJson<TimesheetEntryResponse>('/api/v1/timesheets/entries', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/** Approve a pending edit request (applies the proposed times — server re-runs the lunch math). */
export function useApproveEditRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation<EditRequestActionResponse, Error, { requestId: number }>({
    mutationFn: ({ requestId }) =>
      apiFetchJson<EditRequestActionResponse>(
        `/api/v1/timesheets/edit-requests/${requestId}/approve`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/** Reject a pending edit request (the original entry stands — routed through a confirm dialog). */
export function useRejectEditRequestMutation() {
  const queryClient = useQueryClient();
  return useMutation<EditRequestActionResponse, Error, { requestId: number }>({
    mutationFn: ({ requestId }) =>
      apiFetchJson<EditRequestActionResponse>(
        `/api/v1/timesheets/edit-requests/${requestId}/reject`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/**
 * Resolve an open clock entry (Plan 22-24) — enter the actual clock-out time for a stale open entry.
 * The SPA sends only the raw clock-out; the SERVER recomputes the lunch-adjusted hours + labor cost
 * (math preserved) and enforces the self-or-approve scope + approved-week guard.
 */
export function useResolveOpenMutation() {
  const queryClient = useQueryClient();
  return useMutation<ResolveOpenResponse, Error, { entryId: number } & ResolveOpenInput>({
    mutationFn: ({ entryId, clockOutAt }) =>
      apiFetchJson<ResolveOpenResponse>(`/api/v1/timesheets/${entryId}/resolve-open`, {
        method: 'POST',
        body: JSON.stringify({ clockOutAt }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/**
 * The ADMIN DIRECT EDIT — correct a shift that has already been recorded. The supervisor's own
 * correction path, as opposed to `useRequestEditMutation` below, which is the crew ASKING for one.
 *
 * The SPA sends the clock times and a reason; it never sends an hours figure. The server derives the
 * hours from the span, applies the lunch rule, recomputes labor cost, refuses an approved week, and
 * stamps the row with who edited it and why.
 */
export function useAdminEditEntryMutation() {
  const queryClient = useQueryClient();
  return useMutation<AdminEditEntryResponse, Error, { entryId: number } & AdminEditEntryInput>({
    mutationFn: ({ entryId, ...input }) =>
      apiFetchJson<AdminEditEntryResponse>(`/api/v1/timesheets/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/** Remove a recorded shift outright. Refused inside an approved week — reopen it first. */
export function useDeleteEntryMutation() {
  const queryClient = useQueryClient();
  return useMutation<DeleteEntryResponse, Error, { entryId: number }>({
    mutationFn: ({ entryId }) =>
      apiFetchJson<DeleteEntryResponse>(`/api/v1/timesheets/${entryId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/**
 * Submit a time-correction edit request (Plan 22-24) carrying the EXACT proposed clock-in AND
 * clock-out (not proposedHours only). The SERVER derives the hours from the span + preserves the
 * approved-week + dup-pending guards.
 */
export function useRequestEditMutation() {
  const queryClient = useQueryClient();
  return useMutation<RequestEditResponse, Error, { entryId: number } & RequestEditInput>({
    mutationFn: ({ entryId, ...input }) =>
      apiFetchJson<RequestEditResponse>(`/api/v1/timesheets/${entryId}/request-edit`, {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/**
 * Correct a shift that is STILL RUNNING — move its start time and/or its job while the person is
 * standing on the clock.
 *
 * This sends no hours and no clock-out, and that is the whole safety property: a running shift has
 * no span, so the server writes no figure for it and the pay is still decided by the eventual
 * clock-out. Before this existed an open shift could not be corrected at all — the admin editor
 * prices a shift from both of its times, so it refused one that had only started.
 */
export function useAdminEditOpenEntryMutation() {
  const queryClient = useQueryClient();
  return useMutation<AdminEditEntryResponse, Error, { entryId: number } & AdminEditOpenEntryInput>({
    mutationFn: ({ entryId, ...body }) =>
      apiFetchJson<AdminEditEntryResponse>(`/api/v1/timesheets/${entryId}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}

/**
 * Start a shift for somebody else, at a time that has already passed.
 *
 * What comes back is a REAL open shift, so the crew member clocks themselves out of it at the end
 * of the day and the hours are priced by the ordinary punch path. The server refuses an approved
 * week, someone already on the clock, and any start that would overlap an existing shift — that
 * last one names the shift it collided with, so the caller can say WHICH one is in the way.
 */
export function useClockInForMutation() {
  const queryClient = useQueryClient();
  return useMutation<ClockInForResponse, Error, ClockInForInput>({
    mutationFn: (body) =>
      apiFetchJson<ClockInForResponse>('/api/v1/timesheets/clock-in-for', {
        method: 'POST',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['timesheets'] });
    },
  });
}
